import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { appApi, useGetAllStaffQuery } from "../../../store/appApi";
import { selectUser } from "../../../store/authSlice";
import {
  addMasterCustomerRemarkApi,
  bulkAddMasterCustomersApi,
  getMasterCustomersApi,
  shareMasterCustomersApi,
  updateMasterCustomersApi,
} from "../../../services/staffReporting";
import {
  createMasterCustomer,
  formatDateLabel,
  formatMonthLabel,
  getFormTypeForRole,
  isMasterCustomerLocked,
  LEAD_SOURCE_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  toDateInputValue,
} from "./reportingConfig";
import {
  downloadCustomerTemplate,
  downloadMasterCustomersExcel,
  parseCustomerWorkbook,
} from "./bulkImportCustomers";

// Every role that keeps a customer roster: Tele Calling (formType 3) and Real
// Estate Sales / Manager Real Estate (formType 4).
const isRosterRole = (role) => [3, 4].includes(getFormTypeForRole(role));

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 disabled:bg-gray-50 disabled:text-gray-500";
// The app shell sets `color-scheme: dark`, which paints unstyled inputs dark
// and hides their text — pin the filter bar to a light field.
const FILTER_STYLE = {
  backgroundColor: "#fff",
  color: "#111827",
  colorScheme: "light",
};
const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

const EDITABLE_FIELDS = [
  ["name", "Customer Name"],
  ["phone", "Customer Phone"],
];

const SELECT_FIELDS = [
  ["lead", "Lead", LEAD_SOURCE_OPTIONS],
  ["propertyType", "Type", PROPERTY_TYPE_OPTIONS],
];

const TEXT_FIELDS = [["budget", "Budget"]];

const MasterCustomers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const me = useSelector(selectUser);
  const isAdmin = me?.role === "super-admin";
  const myFormType = getFormTypeForRole(me?.role);

  // Fetched for every visitor (not just admins) — a Tele Calling / Real Estate
  // Sales employee needs this roster too, to pick who to share customers with.
  // Rosters belong to the employee, not a project — a super-admin sees every
  // roster holder, not just the active project's members. Inactive staff are
  // kept (listed last) so their customer history can still be opened.
  const { data: staffData } = useGetAllStaffQuery({
    page: 1,
    limit: 1000,
    allProjects: isAdmin,
  });
  const rosterStaff = useMemo(
    () =>
      (Array.isArray(staffData?.staff) ? staffData.staff : [])
        .filter((s) => isRosterRole(s?.role))
        .sort(
          (a, b) =>
            Number(a.status === false) - Number(b.status === false) ||
            String(a.name).localeCompare(String(b.name)),
        ),
    [staffData],
  );

  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const selectedEmployee = rosterStaff.find(
    (s) => String(s._id) === String(selectedEmployeeId),
  );

  const formType = isAdmin
    ? getFormTypeForRole(selectedEmployee?.role)
    : myFormType;
  const targetEmployeeId = isAdmin ? selectedEmployeeId : "";

  // Employees of the *other* roster type — who a "Share" action can hand
  // customers to (Tele Calling -> Real Estate Sales, or back).
  const shareTargets = useMemo(() => {
    const otherRole =
      formType === 3 ? "Real Estate Sales" : formType === 4 ? "Tele Calling" : "";
    if (!otherRole) return [];
    return rosterStaff.filter(
      (s) => s.role === otherRole && s.status !== false,
    );
  }, [rosterStaff, formType]);

  const [customers, setCustomers] = useState([]);
  // Ids the server has confirmed — a row not in here is a local draft that
  // still needs a Save.
  const [savedIds, setSavedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Row ids currently switched into edit mode for the identity fields
  // (Customer Name/Phone) — everything else renders as text.
  const [editingIds, setEditingIds] = useState(() => new Set());
  // Customer whose full remarks history is open in the popup, if any.
  const [historyCustomer, setHistoryCustomer] = useState(null);
  // Row ids checked for a Share hand-off, plus the picker modal's own state.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTargetId, setShareTargetId] = useState("");
  const [sharing, setSharing] = useState(false);

  // Replace the roster with the server's copy and remember which ids are saved.
  const acceptServerCustomers = (list) => {
    const arr = Array.isArray(list) ? list : [];
    setCustomers(arr);
    setSavedIds(new Set(arr.map((c) => String(c.id))));
    setSelectedIds(new Set());
  };

  // After a roster change, make any open report Detail re-pull its roster.
  const invalidateReports = () =>
    dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
  // Per-row "add a remark" draft: { [customerId]: { text, date } }
  const [remarkDrafts, setRemarkDrafts] = useState({});
  const [addingRemark, setAddingRemark] = useState("");
  // Bulk upload modal
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkWarnings, setBulkWarnings] = useState([]);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const canLoad = formType === 3 || formType === 4;

  useEffect(() => {
    if (!canLoad) {
      setCustomers([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getMasterCustomersApi(formType, targetEmployeeId || undefined)
      .then((res) => {
        if (cancelled) return;
        acceptServerCustomers(res?.data?.customers);
      })
      .catch((error) => {
        if (cancelled) return;
        setCustomers([]);
        Swal.fire({
          icon: "error",
          title: "Could not load customers",
          text:
            error?.response?.data?.message ||
            "The customer roster could not be loaded.",
        });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [canLoad, formType, targetEmployeeId]);

  const updateRow = (id, field, value) =>
    setCustomers((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    );

  // New customers go on top — the freshly-added row should be right where
  // you can see and fill it in, not scrolled off the bottom of the sheet.
  const addRow = () =>
    setCustomers((prev) => [createMasterCustomer(), ...prev]);

  const toggleEdit = (id) =>
    setEditingIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const activeSavedCustomers = customers.filter(
    (c) => !c.archived && savedIds.has(String(c.id)),
  );

  // Date-wise filter for remarks — only show customers with at least one
  // dated remark inside [remarkFrom, remarkTo]. Empty on either side = no
  // restriction on that end.
  const [remarkFrom, setRemarkFrom] = useState("");
  const [remarkTo, setRemarkTo] = useState("");
  const hasRemarkFilter = !!(remarkFrom || remarkTo);
  // Customer name / phone filters (phone matches on digits only, so
  // "98765 43210" finds "+91-9876543210").
  const [nameFilter, setNameFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const nameNeedle = nameFilter.trim().toLowerCase();
  const phoneNeedle = phoneFilter.replace(/\D/g, "");
  const hasTextFilter = !!(nameNeedle || phoneNeedle);
  const hasAnyFilter = hasRemarkFilter || hasTextFilter;
  const visibleCustomers = hasAnyFilter
    ? customers.filter((c) => {
        // Rows being edited or not saved yet always stay on screen, so typing
        // into one never makes it disappear.
        const id = String(c.id);
        if (editingIds.has(id) || !savedIds.has(id)) return true;
        if (nameNeedle && !String(c.name || "").toLowerCase().includes(nameNeedle)) return false;
        if (phoneNeedle && !String(c.phone || "").replace(/\D/g, "").includes(phoneNeedle)) return false;
        if (!hasRemarkFilter) return true;
        return (Array.isArray(c.dailyRemarks) ? c.dailyRemarks : []).some((r) => {
          const d = String(r?.date || "");
          if (!d) return false;
          if (remarkFrom && d < remarkFrom) return false;
          if (remarkTo && d > remarkTo) return false;
          return true;
        });
      })
    : customers;
  const allActiveSelected =
    activeSavedCustomers.length > 0 &&
    activeSavedCustomers.every((c) => selectedIds.has(String(c.id)));
  const toggleSelectAllActive = () =>
    setSelectedIds((prev) => {
      if (allActiveSelected) return new Set();
      return new Set(activeSavedCustomers.map((c) => String(c.id)));
    });

  const openShare = () => {
    setShareTargetId("");
    setShareOpen(true);
  };

  const handleShare = async () => {
    if (!shareTargetId || !selectedIds.size) return;
    try {
      setSharing(true);
      const res = await shareMasterCustomersApi({
        formType,
        ...(targetEmployeeId ? { employeeId: targetEmployeeId } : {}),
        customerIds: Array.from(selectedIds),
        toEmployeeId: shareTargetId,
      });
      acceptServerCustomers(res?.data?.customers);
      invalidateReports();
      setShareOpen(false);
      const destName =
        shareTargets.find((s) => String(s._id) === String(shareTargetId))?.name ||
        "the destination employee";
      Swal.fire({
        icon: "success",
        title: "Shared",
        text: `${res?.data?.moved ?? 0} customer${res?.data?.moved === 1 ? "" : "s"} moved to ${destName}.`,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Share failed",
        text: error?.response?.data?.message || "The customers could not be shared.",
      });
    } finally {
      setSharing(false);
    }
  };

  // A row is "new + unsaved" when the server hasn't confirmed its id yet.
  const isNewUnsaved = (c) => !savedIds.has(String(c?.id));

  const getRemarkDraft = (id) =>
    remarkDrafts[id] || { text: "", date: toDateInputValue() };
  const setRemarkDraft = (id, patch) =>
    setRemarkDrafts((prev) => ({
      ...prev,
      [id]: { ...getRemarkDraft(id), ...patch },
    }));

  // Append a dated remark straight from this page. New customers must be saved
  // (so they exist in the roster) before a remark can be attached.
  const handleAddRemark = async (customer) => {
    const draft = getRemarkDraft(customer.id);
    const text = String(draft.text || "").trim();
    if (!text) return;
    if (isNewUnsaved(customer)) {
      Swal.fire({
        icon: "info",
        title: "Save first",
        text: "Save the new customer before adding a remark.",
      });
      return;
    }
    try {
      setAddingRemark(customer.id);
      const res = await addMasterCustomerRemarkApi({
        formType,
        ...(targetEmployeeId ? { employeeId: targetEmployeeId } : {}),
        customerId: customer.id,
        remarks: text,
        date: draft.date || toDateInputValue(),
      });
      acceptServerCustomers(res?.data?.customers);
      invalidateReports();
      setRemarkDrafts((prev) => ({
        ...prev,
        [customer.id]: { text: "", date: toDateInputValue() },
      }));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not add remark",
        text:
          error?.response?.data?.message || "The remark could not be saved.",
      });
    } finally {
      setAddingRemark("");
    }
  };

  const handleSave = async () => {
    // Drop rows that are completely blank.
    const payloadCustomers = customers.filter((c) =>
      ["name", "phone"].some((k) => String(c?.[k] || "").trim()),
    );
    try {
      setSaving(true);
      const res = await updateMasterCustomersApi({
        formType,
        ...(targetEmployeeId ? { employeeId: targetEmployeeId } : {}),
        customers: payloadCustomers,
      });
      acceptServerCustomers(res?.data?.customers);
      invalidateReports();
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Customer roster updated.",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text:
          error?.response?.data?.message || "Could not save the roster.",
      });
    } finally {
      setSaving(false);
    }
  };

  const openBulk = () => {
    setBulkRows([]);
    setBulkWarnings([]);
    setBulkFileName("");
    setBulkOpen(true);
  };

  const handleBulkFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBulkFileName(file.name);
    try {
      const { rows, warnings } = await parseCustomerWorkbook(file);
      setBulkRows(rows);
      setBulkWarnings(warnings || []);
    } catch (error) {
      setBulkRows([]);
      setBulkWarnings([
        error?.message || "Could not read that file. Use the template format.",
      ]);
    }
  };

  const handleBulkSubmit = async () => {
    if (!bulkRows.length) return;
    try {
      setBulkBusy(true);
      const res = await bulkAddMasterCustomersApi({
        formType,
        ...(targetEmployeeId ? { employeeId: targetEmployeeId } : {}),
        rows: bulkRows,
      });
      acceptServerCustomers(res?.data?.customers);
      invalidateReports();
      setBulkOpen(false);
      Swal.fire({
        icon: "success",
        title: "Customers uploaded",
        text: `${res?.data?.added ?? 0} added, ${res?.data?.skipped ?? 0} skipped (already in the roster).`,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Upload failed",
        text:
          error?.response?.data?.message ||
          "The customers could not be uploaded.",
      });
    } finally {
      setBulkBusy(false);
    }
  };

  const activeCount = customers.filter((c) => !c.archived).length;

  return (
    <>
      <PageTitle activeMenu="Customer Database" motherMenu="Workforce" />
      <div className="space-y-6">
        <section className={`${panelClass} p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Master Customer List
              </div>
              <h1 className="mt-3 text-2xl font-semibold text-gray-900">
                Customer Database
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                The persistent roster every daily report pulls from. Name /
                phone stay editable any time. Use <b>Archive</b> to drop a
                customer from new reports without losing history.
              </p>
            </div>
            {isAdmin && (
              <div className="w-64">
                <button
                  type="button"
                  className="mb-3 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-sky-700 underline-offset-2 hover:underline"
                  onClick={() => navigate("/staff-reporting/efforts")}
                >
                  Team Efforts Report →
                </button>
                <label className={labelClass}>Employee</label>
                <select
                  className={inputClass}
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                >
                  <option value="">Select a Tele Calling / Sales staff…</option>
                  {rosterStaff.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name} · {s.role}
                      {s.status === false ? " (inactive)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </section>

        {!canLoad ? (
          <section className={`${panelClass} p-8 text-center text-gray-500`}>
            {isAdmin
              ? "Pick a Tele Calling or Real Estate Sales employee to manage their customer roster."
              : "The customer database is only available for Tele Calling and Real Estate Sales roles."}
          </section>
        ) : (
          <section className={`${panelClass} p-5 md:p-6`}>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-gray-500">
                {loading
                  ? "Loading…"
                  : `${activeCount} active · ${customers.length - activeCount} archived`}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="search"
                  className="w-44 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 outline-none focus:border-amber-500"
                  style={FILTER_STYLE}
                  placeholder="Customer name"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  aria-label="Filter by customer name"
                />
                <input
                  type="search"
                  inputMode="tel"
                  className="w-36 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 outline-none focus:border-amber-500"
                  style={FILTER_STYLE}
                  placeholder="Phone"
                  value={phoneFilter}
                  onChange={(e) => setPhoneFilter(e.target.value)}
                  aria-label="Filter by customer phone"
                />
                <div className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1">
                  <span className="text-[11px] uppercase tracking-wide text-gray-400">
                    Remarks
                  </span>
                  <input
                    type="date"
                    className="w-32 border-0 bg-white text-xs text-gray-900 outline-none"
                    style={FILTER_STYLE}
                    value={remarkFrom}
                    onChange={(e) => setRemarkFrom(e.target.value)}
                  />
                  <span className="text-xs text-gray-300">–</span>
                  <input
                    type="date"
                    className="w-32 border-0 bg-white text-xs text-gray-900 outline-none"
                    style={FILTER_STYLE}
                    value={remarkTo}
                    onChange={(e) => setRemarkTo(e.target.value)}
                  />
                  {hasRemarkFilter && (
                    <button
                      type="button"
                      className="text-xs text-gray-400 hover:text-gray-600"
                      title="Clear remarks date filter"
                      onClick={() => {
                        setRemarkFrom("");
                        setRemarkTo("");
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                {shareTargets.length > 0 && (
                  <button
                    type="button"
                    className="rounded-full border-2 border-emerald-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-40"
                    onClick={openShare}
                    disabled={loading || !selectedIds.size}
                  >
                    ⇄ Share{selectedIds.size ? ` (${selectedIds.size})` : ""}
                  </button>
                )}
                <button
                  type="button"
                  className="rounded-full border-2 border-sky-400 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-sky-700 transition hover:bg-sky-50"
                  onClick={openBulk}
                  disabled={loading}
                >
                  ⬆ Bulk Upload
                </button>
                <button
                  type="button"
                  className="rounded-full border-2 border-emerald-400 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => downloadMasterCustomersExcel(visibleCustomers, formType)}
                  disabled={loading || visibleCustomers.length === 0}
                >
                  ⬇ Download Excel
                </button>
                <button
                  type="button"
                  className="rounded-full border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-700 transition hover:text-amber-700"
                  onClick={addRow}
                  disabled={loading}
                >
                  + Add Customer
                </button>
                <button
                  type="button"
                  className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleSave}
                  disabled={saving || loading}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    {shareTargets.length > 0 && (
                      <th className="px-3 py-2 text-left font-medium">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          checked={allActiveSelected}
                          onChange={toggleSelectAllActive}
                          title="Select all active customers"
                        />
                      </th>
                    )}
                    {EDITABLE_FIELDS.map(([, label]) => (
                      <th
                        key={label}
                        className="whitespace-nowrap px-3 py-2 text-left font-medium"
                      >
                        {label}
                      </th>
                    ))}
                    <th className="px-3 py-2 text-left font-medium">Edit</th>
                    {SELECT_FIELDS.map(([, label]) => (
                      <th
                        key={label}
                        className="whitespace-nowrap px-3 py-2 text-left font-medium min-w-[140px]"
                      >
                        {label}
                      </th>
                    ))}
                    {TEXT_FIELDS.map(([, label]) => (
                      <th
                        key={label}
                        className="whitespace-nowrap px-3 py-2 text-left font-medium min-w-[130px]"
                      >
                        {label}
                      </th>
                    ))}
                    <th className="whitespace-nowrap px-3 py-2 text-left font-medium">
                      Month
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      Calls / Remarks (log &amp; history)
                    </th>
                    <th className="px-3 py-2 text-left font-medium">Archive</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {!customers.length && !loading && (
                    <tr>
                      <td
                        colSpan={shareTargets.length > 0 ? 10 : 9}
                        className="px-3 py-6 text-center text-xs text-gray-400"
                      >
                        No customers yet — add the first one.
                      </td>
                    </tr>
                  )}
                  {!!customers.length && hasAnyFilter && !visibleCustomers.length && (
                    <tr>
                      <td
                        colSpan={shareTargets.length > 0 ? 10 : 9}
                        className="px-3 py-6 text-center text-xs text-gray-400"
                      >
                        No customer matches these filters.
                      </td>
                    </tr>
                  )}
                  {visibleCustomers.map((c) => {
                    const locked = isMasterCustomerLocked(c);
                    // A freshly added row opens straight into its Name / Phone
                    // inputs, with its own Save button — no "Edit" click needed.
                    const isNew = isNewUnsaved(c);
                    const isEditing = (isNew || editingIds.has(c.id)) && !locked;
                    const history = Array.isArray(c.dailyRemarks)
                      ? [...c.dailyRemarks].sort((a, b) =>
                          String(b.date).localeCompare(String(a.date)),
                        )
                      : [];
                    const latest = history[0];
                    const canSelect = shareTargets.length > 0 && !c.archived && !isNewUnsaved(c);
                    return (
                      <React.Fragment key={c.id}>
                        <tr className={c.archived ? "opacity-50" : undefined}>
                          {shareTargets.length > 0 && (
                            <td className="px-3 py-1.5">
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 disabled:opacity-30"
                                checked={selectedIds.has(String(c.id))}
                                onChange={() => toggleSelect(String(c.id))}
                                disabled={!canSelect}
                                title={
                                  isNewUnsaved(c)
                                    ? "Save the customer first"
                                    : "Select to share"
                                }
                              />
                            </td>
                          )}
                          {EDITABLE_FIELDS.map(([field, label]) =>
                            isEditing ? (
                              <td key={field} className="px-3 py-1.5">
                                <input
                                  className={`${inputClass} min-w-[150px]`}
                                  autoComplete="off"
                                  placeholder={label}
                                  value={c[field] || ""}
                                  onChange={(e) =>
                                    updateRow(c.id, field, e.target.value)
                                  }
                                />
                              </td>
                            ) : (
                              <td
                                key={field}
                                className="px-3 py-1.5 text-sm text-gray-800"
                              >
                                {c[field] || (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            ),
                          )}
                          <td className="whitespace-nowrap px-3 py-1.5">
                            {locked ? (
                              <span
                                className="text-xs text-gray-400"
                                title="Locked after the first remark"
                              >
                                🔒
                              </span>
                            ) : isNew ? (
                              <button
                                type="button"
                                className="rounded-full bg-amber-500 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                                onClick={handleSave}
                                disabled={
                                  saving ||
                                  !["name", "phone"].some((k) =>
                                    String(c[k] || "").trim(),
                                  )
                                }
                                title="Save this customer"
                              >
                                {saving ? "…" : "Save"}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="rounded-full border border-gray-300 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-600 transition hover:border-amber-400 hover:text-amber-700"
                                onClick={() => toggleEdit(c.id)}
                              >
                                {isEditing ? "Done" : "Edit"}
                              </button>
                            )}
                          </td>
                          {SELECT_FIELDS.map(([field, label, options]) => (
                            <td key={field} className="px-3 py-1.5">
                              <select
                                className={`${inputClass} min-w-[140px]`}
                                value={c[field] || ""}
                                onChange={(e) =>
                                  updateRow(c.id, field, e.target.value)
                                }
                              >
                                <option value="">{label}</option>
                                {options.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            </td>
                          ))}
                          {TEXT_FIELDS.map(([field, label]) => (
                            <td key={field} className="px-3 py-1.5">
                              <input
                                className={`${inputClass} min-w-[130px]`}
                                autoComplete="off"
                                placeholder={label}
                                value={c[field] || ""}
                                onChange={(e) =>
                                  updateRow(c.id, field, e.target.value)
                                }
                              />
                            </td>
                          ))}
                          <td className="whitespace-nowrap px-3 py-1.5 text-sm text-gray-800">
                            {formatMonthLabel(c.createdDate)}
                          </td>
                          <td className="px-3 py-1.5">
                            {isNewUnsaved(c) ? (
                              <span className="text-[11px] text-gray-400">
                                Save the customer first
                              </span>
                            ) : (
                              <div className="space-y-1.5">
                                <div className="flex flex-wrap items-center gap-2">
                                  <input
                                    className={`${inputClass} min-w-[180px] flex-1`}
                                    autoComplete="off"
                                    placeholder="Add a remark…"
                                    value={getRemarkDraft(c.id).text}
                                    onChange={(e) =>
                                      setRemarkDraft(c.id, { text: e.target.value })
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") handleAddRemark(c);
                                    }}
                                  />
                                  <input
                                    type="date"
                                    className={`${inputClass} w-36`}
                                    max={toDateInputValue()}
                                    value={getRemarkDraft(c.id).date}
                                    onChange={(e) =>
                                      e.target.value &&
                                      setRemarkDraft(c.id, { date: e.target.value })
                                    }
                                  />
                                  <button
                                    type="button"
                                    className="rounded-full bg-amber-500 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-950 transition hover:bg-amber-400 disabled:opacity-60"
                                    onClick={() => handleAddRemark(c)}
                                    disabled={
                                      addingRemark === c.id ||
                                      !getRemarkDraft(c.id).text.trim()
                                    }
                                  >
                                    {addingRemark === c.id ? "…" : "Add"}
                                  </button>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                  {latest ? (
                                    <span>
                                      <span className="font-medium text-gray-700">
                                        {formatDateLabel(latest.date)}
                                      </span>{" "}
                                      {latest.by && (
                                        <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                                          {latest.by}
                                        </span>
                                      )}{" "}
                                      — {latest.remarks || "(no remark)"}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">
                                      No remarks yet
                                    </span>
                                  )}
                                  {history.length > 0 && (
                                    <button
                                      type="button"
                                      className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 transition hover:bg-amber-200"
                                      onClick={() => setHistoryCustomer(c)}
                                    >
                                      {history.length} remark
                                      {history.length === 1 ? "" : "s"}
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-1.5">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                              checked={Boolean(c.archived)}
                              onChange={(e) =>
                                updateRow(c.id, "archived", e.target.checked)
                              }
                            />
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      {bulkOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => !bulkBusy && setBulkOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">
              Bulk upload customers
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Upload an Excel (.xlsx) or CSV file. Columns (header text, any
              order):
            </p>
            <ul className="mt-2 list-disc pl-5 text-sm text-gray-700">
              <li>
                <b>Customer Name</b>
              </li>
              <li>
                <b>Customer Phone</b>
              </li>
            </ul>
            <button
              type="button"
              className="mt-3 text-xs font-semibold text-sky-700 underline underline-offset-2"
              onClick={() => downloadCustomerTemplate()}
            >
              Download template (.xlsx)
            </button>

            <div className="mt-4">
              <label className={labelClass}>Choose file</label>
              <input
                type="file"
                accept=".xlsx,.csv"
                className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-full file:border-0 file:bg-amber-500 file:px-4 file:py-2 file:text-xs file:font-semibold file:uppercase file:tracking-[0.18em] file:text-slate-950"
                onChange={handleBulkFile}
              />
              {bulkFileName && (
                <p className="mt-1 text-xs text-gray-500">
                  {bulkFileName} — <b>{bulkRows.length}</b> customer
                  {bulkRows.length === 1 ? "" : "s"} ready
                </p>
              )}
            </div>

            {bulkWarnings.length > 0 && (
              <div className="mt-3 max-h-32 overflow-y-auto rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                {bulkWarnings.map((w, i) => (
                  <div key={i}>{w}</div>
                ))}
              </div>
            )}

            {bulkRows.length > 0 && (
              <div className="mt-3 max-h-40 overflow-y-auto rounded-lg border border-gray-200 text-xs">
                <table className="min-w-full">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-2 py-1 text-left">Name</th>
                      <th className="px-2 py-1 text-left">Phone</th>
                      <th className="px-2 py-1 text-left">Lead</th>
                      <th className="px-2 py-1 text-left">Type</th>
                      <th className="px-2 py-1 text-left">Budget</th>
                      <th className="px-2 py-1 text-left">Month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.slice(0, 50).map((r, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-2 py-1">{r.name || "—"}</td>
                        <td className="px-2 py-1">{r.phone || "—"}</td>
                        <td className="px-2 py-1">{r.lead || "—"}</td>
                        <td className="px-2 py-1">{r.propertyType || "—"}</td>
                        <td className="px-2 py-1">{r.budget || "—"}</td>
                        <td className="px-2 py-1">
                          {r.createdDate ? formatDateLabel(r.createdDate) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-full border-2 border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600"
                onClick={() => setBulkOpen(false)}
                disabled={bulkBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleBulkSubmit}
                disabled={bulkBusy || !bulkRows.length}
              >
                {bulkBusy
                  ? "Uploading…"
                  : `Upload ${bulkRows.length || ""} customer${
                      bulkRows.length === 1 ? "" : "s"
                    }`}
              </button>
            </div>
          </div>
        </div>
      )}

      {historyCustomer && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setHistoryCustomer(null)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {historyCustomer.name || historyCustomer.phone || "Customer"}
                </h3>
                <p className="mt-0.5 text-sm text-gray-500">
                  Full remarks / call log history
                </p>
              </div>
              <button
                type="button"
                className="rounded-full border-2 border-gray-300 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600"
                onClick={() => setHistoryCustomer(null)}
              >
                Close
              </button>
            </div>
            <div className="mt-4 max-h-96 overflow-y-auto rounded-lg border border-gray-200">
              {(Array.isArray(historyCustomer.dailyRemarks)
                ? [...historyCustomer.dailyRemarks].sort((a, b) =>
                    String(b.date).localeCompare(String(a.date)),
                  )
                : []
              ).map((h, i) => (
                <div
                  key={i}
                  className={`px-4 py-2 text-sm ${i % 2 === 1 ? "bg-gray-50" : "bg-white"}`}
                >
                  <span className="font-medium text-gray-700">
                    {formatDateLabel(h.date)}
                  </span>{" "}
                  {h.by && (
                    <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                      {h.by}
                    </span>
                  )}{" "}
                  <span className="text-gray-600">
                    — {h.remarks || "(no remark)"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {shareOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => !sharing && setShareOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">
              Share {selectedIds.size} customer{selectedIds.size === 1 ? "" : "s"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              They'll be removed from this roster and moved to the employee you
              pick below — full remarks history included.
            </p>

            <div className="mt-4">
              <label className={labelClass}>
                {formType === 3 ? "Real Estate Sales employee" : "Tele Calling employee"}
              </label>
              <select
                className={inputClass}
                value={shareTargetId}
                onChange={(e) => setShareTargetId(e.target.value)}
              >
                <option value="">Select an employee…</option>
                {shareTargets.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-full border-2 border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600"
                onClick={() => setShareOpen(false)}
                disabled={sharing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-full bg-emerald-600 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleShare}
                disabled={sharing || !shareTargetId}
              >
                {sharing ? "Sharing…" : "Share"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MasterCustomers;
